import React, { useMemo, useState } from 'react';
import { Divider, Input, Row, Col, Select, Space, Spin, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { EditOutlined, EyeOutlined, PlusOutlined, SearchOutlined, HeatMapOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { QaqcPipeWeldtype } from '../../../../types';
import { useGetQaqcPipeWeldtype } from '../../../../hooks/genericHooks';
import StandardTable from '../../../../components/common/StandardTable';
import { useTablePagination } from '../../../../components/common/useTablePagination';
import Button from '../../../../components/common/Button';

const { Title } = Typography;

const QaqcPipeWeldtypeList: React.FC = () => {
  const navigate = useNavigate();
  const { data: resp, isLoading } = useGetQaqcPipeWeldtype();
  const rows: QaqcPipeWeldtype[] = useMemo(() => (Array.isArray((resp as any)?.data) ? (resp as any).data : (Array.isArray(resp) ? (resp as any) : [])) as QaqcPipeWeldtype[], [resp]);

  // Filters
  const [shortNameSearch, setShortNameSearch] = useState('');
  const [longNameSearch, setLongNameSearch] = useState('');
  const [kindFilter, setKindFilter] = useState<number | undefined>();

  // Pagination
  const { getPaginationProps } = useTablePagination(20);

  // Derived
  const baseFilteredRows = useMemo(() => {
    let out = rows;
    const qs = shortNameSearch.trim().toLowerCase();
    if (qs) {
      out = out.filter(r => (r.weldtype_name || '').toLowerCase().includes(qs));
    }
    const ql = longNameSearch.trim().toLowerCase();
    if (ql) {
      out = out.filter(r => (r.weldtype_name_long || '').toLowerCase().includes(ql));
    }
    return out;
  }, [rows, shortNameSearch, longNameSearch]);

  const kindOptions = useMemo(() => {
    const set = new Set<number>();
    baseFilteredRows.forEach(r => Number.isFinite(Number(r.weldtype_kind)) && set.add(Number(r.weldtype_kind)));
    return [{ label: 'All kinds', value: '' }, ...Array.from(set).sort((a,b)=>a-b).map(v => ({ label: String(v), value: v }))];
  }, [baseFilteredRows]);

  const filteredData = useMemo(() => {
    return baseFilteredRows.filter(r => kindFilter === undefined || kindFilter === null || kindFilter === (r.weldtype_kind as number));
  }, [baseFilteredRows, kindFilter]);

  // Actions
  const handleCreate = () => navigate('/qaqc/piping/tools/weldtypes/create');
  const handleView = (id: number) => navigate(`/qaqc/piping/tools/weldtypes/view/${id}`);
  const handleEdit = (id: number) => navigate(`/qaqc/piping/tools/weldtypes/edit/${id}`);

  // Columns
  const columns: ColumnsType<QaqcPipeWeldtype> = [
    { title: 'Weldtype', dataIndex: 'weldtype_name', key: 'weldtype_name', width: 120, sorter: (a,b)=>a.weldtype_name.localeCompare(b.weldtype_name) },
    { title: 'Long Name', dataIndex: 'weldtype_name_long', key: 'weldtype_name_long', width: 260 },
    { title: 'Factor', dataIndex: 'weldtype_factor', key: 'weldtype_factor', width: 100, sorter: (a,b)=>Number(a.weldtype_factor)-Number(b.weldtype_factor) },
    { title: 'Kind', dataIndex: 'weldtype_kind', key: 'weldtype_kind', width: 80 },
    { title: 'PT', dataIndex: 'pt_flag', key: 'pt_flag', width: 60, render: v => v ? 'Yes' : '' },
    { title: 'RT', dataIndex: 'rt_flag', key: 'rt_flag', width: 60, render: v => v ? 'Yes' : '' },
    { title: 'MT', dataIndex: 'mt_flag', key: 'mt_flag', width: 60, render: v => v ? 'Yes' : '' },
    { title: 'UT', dataIndex: 'ut_flag', key: 'ut_flag', width: 60, render: v => v ? 'Yes' : '' },
    {
      title: 'Action', key: 'action', align: 'center', width: 120,
      render: (_, r) => (
        <Space>
          <Button action="read" icon={<EyeOutlined />} onClick={() => handleView((r as any).id as number)} />
          <Button action="edit" type="primary" icon={<EditOutlined />} onClick={() => handleEdit((r as any).id as number)} />
        </Space>
      )
    },
  ];

  return (
    <>
      <Divider style={{ marginTop: 0 }} />
      <div style={{ padding: '0 24px' }}>
        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
          <Col>
            <Title level={3} style={{ margin: 0 }}>
              <HeatMapOutlined style={{ marginRight: 8 }} /> QAQC Pipe Weld Types
            </Title>
          </Col>
          <Col>
            <Button action="add" type="primary" icon={<PlusOutlined />} onClick={handleCreate}>New Weldtype</Button>
          </Col>
        </Row>

        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Input
              allowClear
              placeholder="Search by name"
              prefix={<SearchOutlined />}
              value={shortNameSearch}
              onChange={(e) => setShortNameSearch(e.target.value)}
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Input
              allowClear
              placeholder="Search by long name"
              prefix={<SearchOutlined />}
              value={longNameSearch}
              onChange={(e) => setLongNameSearch(e.target.value)}
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Select
              allowClear
              showSearch
              placeholder="Filter by kind"
              value={kindFilter as any}
              onChange={(val) => setKindFilter((val as any) === '' ? undefined : (val as number))}
              options={kindOptions}
              optionFilterProp="label"
              style={{ width: '100%' }}
            />
          </Col>
        </Row>

        <Spin spinning={isLoading}>
          <StandardTable
            columns={columns as any}
            dataSource={filteredData}
            rowKey={(r: any) => String(r.id)}
            loading={isLoading}
            pagination={getPaginationProps(filteredData.length)}
            scroll={{ x: true }}
            bordered
            size="small"
          />
        </Spin>
      </div>
    </>
  );
};

export default QaqcPipeWeldtypeList;
